import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button as TamaguiButton, XStack, YStack } from 'tamagui';

import {
  AppButton,
  AppCard,
  AppCardBody,
  AppDisclosureGlyph,
  AppGuideCardButton,
  AppSectionHeader,
  AppText,
} from '../../../ui-foundation';
import {
  buildGuideRailLaunchSummary,
  GUIDE_HUB_META,
  GUIDE_SURFACE_META,
  type GuideSurfaceSection,
} from '../../../ui-foundation/features/guides';
import {
  buildGuideDetailPath,
  capitalise,
  fallbackHelperPackIcon,
  helperPackFooterLabel,
  splitGuideItems,
  useGuides,
} from './guideData';

export default function SectionGuideRail({
  section,
  showStartHere = false,
}: {
  section: GuideSurfaceSection;
  showStartHere?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { guides, loading, error } = useGuides(section);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [supportCardsOpen, setSupportCardsOpen] = useState(false);
  const from = `${location.pathname}${location.search}`;
  const { helperPacks, supportCards } = useMemo(() => splitGuideItems(guides), [guides]);
  const quickStart = useMemo(
    () => (showStartHere ? buildGuideRailLaunchSummary(section, guides) : null),
    [guides, section, showStartHere],
  );

  if (!loading && !guides.length) {
    return null;
  }

  return (
    <section className="space-y-2">
      <AppSectionHeader
        title={GUIDE_SURFACE_META[section].hubTitle}
        subtitle={error || undefined}
        action={(
          <AppButton size="sm" tone="outline" onPress={() => navigate(`/guides?section=${section}`)}>
            Open support hub
          </AppButton>
        )}
      />

      {loading ? (
        <AppText color="$colorMuted" fontSize="$sm">
          Loading support hub...
        </AppText>
      ) : (
        <div className="space-y-2">
          {quickStart ? (
            <AppCard surface="secondary">
              <AppCardBody>
                <YStack gap="$sm">
                  <YStack gap="$xs">
                    <AppText color="$colorMuted" fontSize="$xs" fontWeight="700">
                      Start here
                    </AppText>
                    <AppText fontSize="$sm" lineHeight="$sm" fontWeight="700">
                      {quickStart.title}
                    </AppText>
                    <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                      {quickStart.body}
                    </AppText>
                    {quickStart.scriptHint ? (
                      <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                        {quickStart.scriptHint}
                      </AppText>
                    ) : null}
                  </YStack>
                  <XStack alignItems="center" justifyContent="space-between" gap="$sm" flexWrap="wrap">
                    <AppButton
                      size="sm"
                      tone="outline"
                      onPress={() =>
                        navigate(`/guides/${section}/${quickStart.itemKind}/${quickStart.itemId}`, {
                          state: { from },
                        })
                      }
                    >
                      {quickStart.buttonLabel}
                    </AppButton>
                    <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                      {quickStart.meta}
                    </AppText>
                  </XStack>
                </YStack>
              </AppCardBody>
            </AppCard>
          ) : null}

          {helperPacks.length ? (
            <AppCard surface="secondary">
              <AppCardBody>
                <YStack gap="$sm">
                  <YStack gap="$xs">
                    <AppText fontSize="$sm" lineHeight="$sm" fontWeight="700">
                      {GUIDE_HUB_META.helperPacksTitle}
                    </AppText>
                    <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                      {GUIDE_SURFACE_META[section].helperPackSubtitle} {helperPacks.length} ready to open.
                    </AppText>
                  </YStack>
                  <div className="overflow-x-auto pb-1">
                    <div className="flex min-w-max gap-3 pr-1">
                    {helperPacks.map((guide) => (
                      <AppGuideCardButton
                        type="button"
                        key={`${guide.kind}-${guide.id}`}
                        minHeight={104}
                        className="w-[216px] shrink-0"
                        onPress={() =>
                          navigate(buildGuideDetailPath(section, guide), {
                            state: { from },
                          })
                        }
                      >
                        <YStack flex={1} justifyContent="space-between" gap="$sm">
                          <YStack gap="$sm">
                            <XStack alignItems="flex-start" gap="$sm">
                              <AppText color="$primaryForeground" fontSize={26} lineHeight={28}>
                                {guide.icon || fallbackHelperPackIcon(guide.title)}
                              </AppText>
                              <AppText
                                flex={1}
                                color="$primaryForeground"
                                fontSize="$sm"
                                lineHeight="$sm"
                                fontWeight="700"
                              >
                                {guide.title}
                              </AppText>
                            </XStack>
                            <AppText color="$primaryForeground" opacity={0.82} fontSize="$sm" lineHeight={18}>
                              {guide.description}
                            </AppText>
                          </YStack>
                          <AppText color="$primaryForeground" opacity={0.68} fontSize="$xs" lineHeight={16} fontWeight="600">
                            {guide.difficulty
                              ? `${capitalise(guide.difficulty)} • ${helperPackFooterLabel(guide)}`
                              : helperPackFooterLabel(guide)}
                          </AppText>
                        </YStack>
                      </AppGuideCardButton>
                    ))}
                    </div>
                  </div>
                </YStack>
              </AppCardBody>
            </AppCard>
          ) : null}

          {supportCards.length ? (
            <AppCard surface="secondary">
              <TamaguiButton
                unstyled
                type="button"
                width="100%"
                onPress={() => setSupportCardsOpen((current) => !current)}
                pressStyle={{ opacity: 0.94 }}
              >
                <AppCardBody>
                  <XStack alignItems="center" justifyContent="space-between" gap="$sm">
                    <YStack flex={1} gap="$xs" paddingRight="$sm">
                      <AppText fontSize="$sm" lineHeight="$sm" fontWeight="700">
                        {GUIDE_HUB_META.supportCardsTitle}
                      </AppText>
                      <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                        {GUIDE_HUB_META.supportCardsSubtitle} {supportCards.length} ready to browse.
                      </AppText>
                    </YStack>
                    <AppDisclosureGlyph open={supportCardsOpen} />
                  </XStack>
                </AppCardBody>
              </TamaguiButton>

              {supportCardsOpen ? (
                <AppCardBody pt={0}>
                  <div className="overflow-hidden rounded-2xl border border-border bg-background/80">
                  {supportCards.map((guide) => {
                    const isExpanded = expandedCardId === guide.id;

                    return (
                      <div
                        key={`${guide.kind}-${guide.id}`}
                        className="border-t border-border/80 first:border-t-0"
                      >
                        <TamaguiButton
                          unstyled
                          type="button"
                          width="100%"
                          onPress={() => setExpandedCardId(isExpanded ? null : guide.id)}
                          pressStyle={{ opacity: 0.94 }}
                        >
                          <AppCardBody px="$sm" py="$sm">
                            <XStack alignItems="center" justifyContent="space-between" gap="$sm">
                              <AppText flex={1} paddingRight="$sm" fontSize="$sm" lineHeight="$sm" fontWeight="700">
                                {guide.title}
                              </AppText>
                              <AppDisclosureGlyph open={isExpanded} />
                            </XStack>
                          </AppCardBody>
                        </TamaguiButton>

                        {isExpanded ? (
                          <AppCardBody pt={0} px="$sm" py="$sm" gap="$xs" borderTopWidth={1} borderTopColor="$borderColor">
                            {guide.summary ? (
                              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md" whiteSpace="pre-wrap">
                                {guide.summary}
                              </AppText>
                            ) : null}
                            <AppText fontSize="$sm" lineHeight="$md" whiteSpace="pre-wrap">
                              {guide.body}
                            </AppText>
                            {guide.detail ? (
                              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md" whiteSpace="pre-wrap">
                                {guide.detail}
                              </AppText>
                            ) : null}
                            {guide.link_url ? (
                              <a
                                href={guide.link_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-semibold text-primary underline-offset-4 hover:underline"
                              >
                                {guide.link_label || 'Find out more'}
                              </a>
                            ) : null}
                            {guide.last_reviewed_at ? (
                              <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                                Last reviewed: {format(new Date(guide.last_reviewed_at), 'd MMMM yyyy')}
                              </AppText>
                            ) : null}
                          </AppCardBody>
                        ) : null}
                      </div>
                    );
                  })}
                  </div>
                </AppCardBody>
              ) : null}
            </AppCard>
          ) : null}
        </div>
      )}
    </section>
  );
}
